import { type DashboardScreenGame, opponentColor } from '@words-happen/screens';
import type { GamesList, GamesListGame } from '../api/games';

/** The most games the dashboard lists; the rest are on /games. */
export const DASHBOARD_GAMES_LIMIT = 5;

/** The name shown for an opponent the API names nobody, as on /games. */
export const UNKNOWN_OPPONENT_NAME = 'Unknown';

const status = ({ isMyTurn }: GamesListGame, finished: boolean): DashboardScreenGame['status'] => {
  if (finished) return 'ended';
  return isMyTurn ? 'your-turn' : 'their-turn';
};

const lastMove = (game: DashboardScreenGame): string | undefined => {
  if (game.status === 'your-turn') return 'Your move';
  if (game.status === 'their-turn') return `Waiting on ${game.opponent.name}`;
  return undefined;
};

const row = (game: GamesListGame, finished: boolean, onSelect: (gameId: string) => void): DashboardScreenGame => {
  const base: DashboardScreenGame = {
    id: game.id,
    opponent: { name: game.opponentName ?? UNKNOWN_OPPONENT_NAME, color: opponentColor(game.id) },
    status: status(game, finished),
  };
  return {
    ...base,
    lastMove: lastMove(base),
    score: base.status === 'ended' ? game.myScore : undefined,
    onSelect: () => onSelect(game.id),
  };
};

/**
 * The dashboard's "Your games": active games in the API's order (the player's turn first), then finished ones, at
 * most `DASHBOARD_GAMES_LIMIT`. Waiting games have no opponent yet, so they are left to /games.
 */
export const dashboardGames = (
  { active, finished }: Pick<GamesList, 'active' | 'finished'>,
  onSelect: (gameId: string) => void,
): DashboardScreenGame[] =>
  [...active.map((game) => row(game, false, onSelect)), ...finished.map((game) => row(game, true, onSelect))].slice(
    0,
    DASHBOARD_GAMES_LIMIT,
  );
